import { useCallback, useMemo } from 'react';

import { Reward } from 'features/reward/types';
import { isEngineReward } from 'features/reward/utils';
import { useStoreContext } from 'features/store';

import { ISanityVendorConfigs } from '@rbi-ctg/menu';
import { ItemAvailabilityStatus } from 'enums/menu';
import { Maybe } from 'generated/sanity-graphql';
import usePosVendor from 'hooks/menu/use-pos-vendor';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { selectors, useAppSelector } from 'state/global-state';
import { LoyaltyOffer, LoyaltyReward, isLoyaltyOffer } from 'state/loyalty/types';
import { isDiscountOffer } from 'state/loyalty/utils';
import { useMenuContext } from 'state/menu';
import { useMainMenuContext } from 'state/menu/main-menu';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { isEntryAvailable } from 'state/unified-menu/utils';
import { itemIsAvailable } from 'utils/availability';
import { PosVendors } from 'utils/vendor-config';

import {
  checkIncentiveAvailableForDayPart,
  makeIncentiveUnavailableMessage,
} from './incentive-details/utils';

// Discount offers (e.g. a "$X off your order" complete-purchase discount) apply
// at the order level and aren't tied to a specific menu item's PLU/vendorConfig,
// so it's expected (not a real unavailability signal) for their `vendorConfigs`
// to have no entry at all for the current vendor — running THAT through
// `itemIsAvailable` reports a false unavailable and gets a valid offer silently
// evicted. This used to only be skipped for NCR/QDI, but the same missing-entry
// mismatch applies regardless of vendor.
//
// However, `itemIsAvailable` can't distinguish "no entry for this vendor" from
// "entry exists but its PLU/price data is missing" (both return `false` — see
// `getVendorConfig`/`vendorConfigIsAvailable` in `utils/availability`). If the
// CMS content DOES have an entry for the current vendor, that's a deliberate
// per-vendor configuration, and its price data is the real signal for whether
// the discount is usable at that vendor/POS — so only skip when there's truly
// no entry to evaluate, not whenever the offer happens to be discount-type.
const shouldSkipAvailabilityCheck = ({
  offer,
  vendor,
}: {
  offer: LoyaltyOffer;
  vendor: PosVendors | null;
}) => {
  if (!isDiscountOffer(offer) || !vendor) {
    return false;
  }
  const vendorConfigs = offer.vendorConfigs as ISanityVendorConfigs | undefined;
  return vendorConfigs?.[vendor] == null;
};

type DetailIncentive = LoyaltyReward | LoyaltyOffer | Reward;

type GetIncentiveUnavailableMessageInput = {
  incentive: DetailIncentive;
  pointCost?: number;
  locked?: Maybe<boolean>;
};

export const useLoyaltyIncentivesAvailabilityUtils = () => {
  const {
    checkItemAvailability,
    showBurgersForBreakfast: burgersForBreakfastEnabled,
  } = useMenuContext();
  const { activeDayParts, dayParts } = useMainMenuContext();
  const { isStoreOpenAndAvailable, prices } = useStoreContext();
  const { vendor } = usePosVendor();
  const stagedCartPoints = useAppSelector(selectors.loyalty.selectStagedCartPoints);
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { indexedMenu, indexedRewards, isLoading: isUnifiedMenuLoading } = useUnifiedMenuContext();
  const isUnifiedMenuLoaded = indexedMenu.isLoaded();
  const shouldUseUnifiedMenuAvailability = isUnifiedMenuEnabled && isUnifiedMenuLoaded;
  const shouldWaitForUnifiedMenuAvailability =
    isUnifiedMenuEnabled && isUnifiedMenuLoading && !isUnifiedMenuLoaded;
  const { posServiceMode } = useMenuQueryOptions();

  const isEntityAvailable = useCallback(
    (entityId: string, ignoreDayPart = false) => {
      const activeDayPartIds = ignoreDayPart ? undefined : activeDayParts.map(d => d.key);
      const rewardEntry = indexedRewards.find(entityId);
      if (rewardEntry) {
        if (rewardEntry.availability?.[posServiceMode] === false) {
          return false;
        }

        const option = rewardEntry.options?.[0];
        if (!option) {
          return false;
        }

        const menuEntry = indexedMenu.find(option.entryId);
        if (!menuEntry) {
          return false;
        }

        return isEntryAvailable(menuEntry, posServiceMode, activeDayPartIds);
      }

      const menuEntry = indexedMenu.find(entityId);
      return !!menuEntry && isEntryAvailable(menuEntry, posServiceMode, activeDayPartIds);
    },
    [activeDayParts, indexedMenu, indexedRewards, posServiceMode]
  );

  const checkIncentiveAvailability = useCallback(
    async (itemId: string, options?: { ignoreDayPart?: boolean }) => {
      if (shouldWaitForUnifiedMenuAvailability) {
        return true;
      }

      if (shouldUseUnifiedMenuAvailability) {
        const [, ...sanitizedId] = itemId.split('-');
        const ignoreDayPart = options?.ignoreDayPart ?? false;
        return (
          isEntityAvailable(itemId, ignoreDayPart) ||
          isEntityAvailable(sanitizedId.join('-'), ignoreDayPart)
        );
      }

      // Check store availability for incentive
      if (isStoreOpenAndAvailable && itemId) {
        const { availabilityStatus } = await checkItemAvailability(itemId);

        return availabilityStatus === ItemAvailabilityStatus.AVAILABLE;
      }
      return false;
    },
    [
      checkItemAvailability,
      isEntityAvailable,
      isStoreOpenAndAvailable,
      shouldUseUnifiedMenuAvailability,
      shouldWaitForUnifiedMenuAvailability,
    ]
  );

  const checkLoyaltyIncentiveAvailability = useCallback(
    (incentive: DetailIncentive) => {
      if (shouldWaitForUnifiedMenuAvailability) {
        return true;
      }

      if (shouldUseUnifiedMenuAvailability) {
        return isEntityAvailable(
          isEngineReward(incentive) ? incentive.cmsId ?? incentive.id : incentive._id
        );
      }
      let checkResult = true;

      if (!!incentive?.vendorConfigs && !!vendor && !!prices) {
        checkResult = itemIsAvailable(
          {
            ...incentive,
            name: undefined,
            vendorConfigs: incentive.vendorConfigs as ISanityVendorConfigs,
          },
          vendor,
          prices
        );
      }

      return checkResult;
    },
    [
      isEntityAvailable,
      prices,
      shouldUseUnifiedMenuAvailability,
      shouldWaitForUnifiedMenuAvailability,
      vendor,
    ]
  );

  const checkLoyaltyOfferAvailability = useCallback(
    (offer: LoyaltyOffer) => {
      return !vendor || shouldSkipAvailabilityCheck({ offer, vendor })
        ? true
        : checkLoyaltyIncentiveAvailability(offer);
    },
    [vendor, checkLoyaltyIncentiveAvailability]
  );

  const getIncentiveUnavailableMessage = useCallback(
    ({ incentive, pointCost, locked }: GetIncentiveUnavailableMessageInput) => {
      if (!incentive) {
        return;
      }
      const isIncentiveAvailable = isLoyaltyOffer(incentive)
        ? checkLoyaltyOfferAvailability(incentive)
        : checkLoyaltyIncentiveAvailability(incentive);

      const isAvailableForDayPart = checkIncentiveAvailableForDayPart({
        activeDayParts,
        burgersForBreakfastEnabled,
        incentive,
      });

      const incentiveUnavailableMessage = makeIncentiveUnavailableMessage({
        burgersForBreakfastEnabled,
        dayParts,
        incentive,
        isAvailableForDayPart,
        isIncentiveAvailable,
        locked,
        pointCost,
        stagedCartPoints,
      });

      return incentiveUnavailableMessage;
    },
    [
      activeDayParts,
      burgersForBreakfastEnabled,
      checkLoyaltyIncentiveAvailability,
      checkLoyaltyOfferAvailability,
      dayParts,
      stagedCartPoints,
    ]
  );

  // When dependencies have been loaded
  const ready = useMemo(() => !!activeDayParts.length, [activeDayParts]);

  return {
    checkIncentiveAvailability,
    checkLoyaltyOfferAvailability,
    getIncentiveUnavailableMessage,
    ready,
  };
};

export const useLoyaltyIncentivesAvailability = (
  incentive?: DetailIncentive,
  pointCost?: number,
  locked?: Maybe<boolean>
) => {
  const {
    checkIncentiveAvailability,
    checkLoyaltyOfferAvailability,
    getIncentiveUnavailableMessage,
  } = useLoyaltyIncentivesAvailabilityUtils();

  const incentiveUnavailableMessage = useMemo(
    () => incentive && getIncentiveUnavailableMessage({ locked, pointCost, incentive }),
    [getIncentiveUnavailableMessage, locked, pointCost, incentive]
  );

  return {
    checkIncentiveAvailability,
    checkLoyaltyOfferAvailability,
    incentiveUnavailableMessage,
  };
};

export type CheckLoyaltyOfferAvailabilityType = ReturnType<
  typeof useLoyaltyIncentivesAvailabilityUtils
>['checkLoyaltyOfferAvailability'];
